/* eslint-disable @typescript-eslint/no-explicit-any */
import { MenuItem, OutlinedInput } from "@mui/material"
import { SelectChangeEvent } from "@mui/material/Select"
import { useQuery } from "@tanstack/react-query"
import { useCallback, useMemo, useState } from "react"
import giftApis from "src/api/gifts"
import { GIFTS_BY_CUSTOMER } from "src/constants/queryKeys"
import { useAuthContext } from "src/hooks"
import { theme } from "src/providers/ThemeProvider"
import { Button, SelectField, Text } from "src/shared"
import { GiftListByCustomerModel } from "src/types"
import GiftSelectItem from "./GiftSelectItem"
import PlacehoderItem from "./PlacehoderItem"

interface GiftSelectionProps {
  value: Array<string>
  onChange: (val?: Array<GiftListByCustomerModel>) => void
  stockCode?: string
}

export interface GiftSelectOption extends GiftListByCustomerModel {
  value: string
  label: string
}

const GiftSelection = ({
  value: selectedValue = [],
  onChange,
  stockCode,
}: GiftSelectionProps) => {
  const { profile } = useAuthContext()

  const [open, setOpen] = useState(false)

  const { data } = useQuery({
    queryKey: [GIFTS_BY_CUSTOMER, stockCode],
    queryFn: () =>
      giftApis.getGiftsByCustomer({
        customerPhone: profile?.Mobile || "",
        pageIndex: 0,
        pageSize: 100,
        isChanged: -1,
        stockCode: stockCode || "",
      }),
    enabled: !!profile?.Mobile && !!stockCode,
  })

  const options = useMemo(() => {
    if (!data || !data.Result || !Array.isArray(data.Result)) return []
    const mapOptions = data.Result.map(
      x =>
        ({
          ...x,
          label: x.ProductName,
          value: x.ProductCode,
        }) as GiftSelectOption
    )

    const filteredOptions = [] as Array<GiftSelectOption>
    mapOptions.forEach(x => {
      if (!filteredOptions.some(s => s.ProductCode === x.ProductCode)) {
        filteredOptions.push(x)
      }
    })
    return filteredOptions
  }, [data])

  const renderValue = useCallback(
    (selectedVal: Array<string>) => {
      if (selectedVal.length === 0) {
        return (
          <Text
            sx={{
              color: theme.palette.grey[400],
              fontSize: 16,
              fontWeight: 400,
              lineHeight: "21px",
            }}
          >
            Chọn quà tặng
          </Text>
        )
      }

      const getLabel = options.filter(c => selectedVal.includes(c.value))

      return (
        <Text
          lineHeight="21px"
          color={theme.palette.common.black}
          fontSize={16}
          textOverflow="ellipsis"
          overflow="hidden"
        >
          {getLabel.map(x => x.label).join(", ")}
        </Text>
      )
    },
    [options]
  )

  const handleChange = useCallback(
    (event: SelectChangeEvent<typeof selectedValue>) => {
      const {
        target: { value },
      } = event
      const vals =
        typeof value === "string" ? value.split(",") : value.filter(v => !!v)
      if (vals.includes("remove")) {
        onChange(undefined)
        return
      }
      const valSelect = options.filter(x => vals.includes(x.ProductCode))
      onChange(valSelect)
    },
    [onChange, options]
  )

  const onOpen = () => setOpen(true)

  const onClose = () => setOpen(false)

  return (
    <SelectField
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      multiple
      MenuProps={{
        marginThreshold: 0,
        sx: {
          "& .MuiPaper-root": {
            borderRadius: theme.spacing(3.75, 3.75, 0, 0),
            maxWidth: 526,
            [theme.breakpoints.down(600)]: {
              maxWidth: "100%",
              width: "100%",
            },
          },
          "& .MuiList-root": {
            padding: "0px",
            position: "relative",
            "& > li": {
              padding: theme.spacing(2.75, 2.5),
              [theme.breakpoints.down(640)]: {
                padding: theme.spacing(1.5),
              },
            },
          },
        },
      }}
      options={options}
      onChange={handleChange}
      displayEmpty
      value={selectedValue}
      renderValue={renderValue}
      input={<OutlinedInput />}
    >
      <MenuItem
        onClick={onClose}
        sx={{
          cursor: "default",
          backgroundColor: `${theme.palette.common.white} !important`,
          position: "sticky",
          top: 0,
          zIndex: 999,
        }}
        value="remove"
      >
        <PlacehoderItem profile={profile} />
      </MenuItem>
      {options
        .filter(o => o.value)
        .map((o, i) => {
          const disabled = o.IsChange !== 1
          return (
            <MenuItem
              sx={{
                borderTop: `solid 1px ${theme.palette.grey[200]}`,
                "&.MuiMenuItem-root.Mui-selected": {
                  bgcolor: theme.palette.common.white,
                },
                "&.MuiMenuItem-root.Mui-disabled": {
                  opacity: 0.5,
                },
              }}
              disabled={disabled}
              key={`${o.value}-${i}`}
              value={o.value}
            >
              <GiftSelectItem
                isChecked={selectedValue.some(s => s.includes(o.value))}
                data={o}
                disabled={disabled}
              />
            </MenuItem>
          )
        })}
      {options && options.length ? (
        <MenuItem
          onClick={onClose}
          sx={{
            cursor: "default",
            backgroundColor: `${theme.palette.info.main} !important`,
            position: "sticky",
            bottom: 0,
            zIndex: 999,
            justifyContent: "center",
          }}
        >
          <Button
            width={238}
            sx={{
              bgcolor: theme.palette.common.white,
              color: theme.palette.info.main,
              "&:hover": {
                bgcolor: theme.palette.grey[100],
              },
              "& > p": {
                fontSize: 16,
                fontWeight: 400,
                lineHeight: "21ox",
                textTransform: "none",
              },
              [theme.breakpoints.down(640)]: {
                width: 140,
              },
            }}
          >
            Áp dụng
          </Button>
        </MenuItem>
      ) : null}
    </SelectField>
  )
}

export default GiftSelection
